import { getSettings } from './getSettings';
import { getScenarioFromIframeSource, getIframesForScenarioV2 } from './iframeScenarioUtils';
import { prettyPrintFrameInfos } from './tracing';
import { trace } from 'owa-trace';
import {
    getFrameInfosV2,
    type FrameInfoV2,
    type HighMemoryInfoV2,
} from '@microsoft/wv2-outlook/instanceIframeTracker';
import type { IframeScenario } from '../settings/iframes-mapping';
import type { IframeState } from '../hooks/useIframeState';
import { getIframeInfo, type IframeInfo } from './getIframeInfo';

type ProcessId = number;
type CorrelationId = string;

type IframeScenarioKey = IframeScenario | 'Unknown'; // only for caching iframe info with unknown scenario

export interface FrameMemoryMetrics {
    /** The working set size of the iframe frame in KB */
    workingSetSizeKB: number;
    /** The commit size of the iframe frame in KB */
    commitSizeKB: number;
    /** The host and path of the iframe source URL, used for telemetry and debugging */
    hostAndPath: string;
    /** The process ID hosting this iframe frame, used for telemetry and debugging */
    processId: ProcessId;
}

/**
 * General per-scenario memory information.
 */
export interface IframeMemoryInfo {
    scenario: IframeScenarioKey;
    /** Total memory used by all iframes in this scenario. */
    totalMemory: number;
    /** Total commit size used by all iframes in this scenario. */
    totalCommitSize: number;
    /** Detailed memory metrics for each iframe frame. */
    frameMemoryMetrics: FrameMemoryMetrics[];
    processes: Map<ProcessId, ProcessMemory>;
}

/**
 * Memory information for a specific process hosting iframes.
 * Includes the total memory used by that process and the correlation IDs of the iframes it hosts.
 */
interface ProcessMemory {
    processId: ProcessId;
    /**
     * Total memory used by the iframes in this process. Since a process may host multiple iframes,
     * this is the sum of their memory usage. In the future, if we use OOPIFs, we can get per-iframe memory.
     */
    memory: number;
    /**
     * Total commit size used by the iframes in this process.
     */
    commitSize: number;
    /**
     * Correlation IDs of the iframes hosted in this process. This is passed from the native host
     * after parsing the `sessionId` qsp in the iframe src URL.
     * If this qsp is not present, the native host will pass "unavailable", so we can still track count
     * of iframes per process, but correlation to specific iframes is not possible.
     */
    correlationIds: CorrelationId[];
}

/**
 * Memory telemetry fields for logging. Preserves the same format as the old
 * updateCachedMemoryIframeInfo() to maintain telemetry compatibility.
 */
export interface MemoryTelemetryFields {
    /** Total memory used by the processes hosting iframes for this scenario (in MB) */
    totalScenarioMemory: number;
    /** Number of distinct processes hosting iframes for this scenario */
    processCount: number;
    /** Comma-separated process IDs that host iframes for this scenario */
    processIds: string;
    /** Comma-separated iframe counts per process for this scenario */
    scenarioIframeCounts: string;
    /**
     * Comma-separated total iframe counts for processes that host iframes for this scenario
     * (including iframes from other scenarios). This helps understand process sharing across scenarios.
     */
    totalIframeCounts: string;
}

const scenarioMemoryCache = new Map<IframeScenarioKey, IframeMemoryInfo>();

/**
 * Refreshes the memory cache from native host, updating *all* scenarios.
 */
async function refreshMemoryCacheFromNativeHost(): Promise<boolean> {
    // Outside of Monarch, getFrameInfosV2 is undefined
    if (!getFrameInfosV2) {
        return false;
    }

    try {
        const iframes = await getFrameInfosV2();
        updateCacheFromFrameInfos(iframes);
        trace.info(
            ` Refreshed iframe memory cache from native host.\n` + prettyPrintFrameInfos(iframes),
            'iframeWrapper'
        );
        return true;
    } catch {
        return false;
    }
}

/**
 * Gets iframe memory info for a single scenario from native host.
 * Cache is updated automatically.
 *
 * @param scenario - The scenario to get memory for
 * @returns IframeMemoryInfo or undefined if not available
 */
export async function getIframeMemoryInfo(
    scenario: IframeScenario
): Promise<IframeMemoryInfo | undefined> {
    await refreshMemoryCacheFromNativeHost();
    return scenarioMemoryCache.get(scenario);
}

/**
 * Gets iframe memory info for ALL scenarios from native host and updates cache.
 * Cache is updated automatically.
 */
export async function getAllIframeMemoryInfo(): Promise<
    ReadonlyMap<IframeScenarioKey, IframeMemoryInfo> | undefined
> {
    const success = await refreshMemoryCacheFromNativeHost();

    if (!success) {
        trace.warn('Could not get total iframe memory info.', 'iframeWrapper');
        return undefined;
    }

    return scenarioMemoryCache;
}

/**
 * Checks if memory usage exceeds configured thresholds.
 *
 * @param memory - Memory value in MB to check
 * @param state - Current iframe state (affects which threshold is used)
 * @returns true if memory exceeds threshold for the given state
 */
export function checkIfMemoryIsAboveThreshold(
    memory: number | undefined,
    state: IframeState
): boolean {
    if (typeof memory !== 'number') {
        return false;
    }

    if (state === 'PRELOAD-HIDDEN') {
        return memory > getSettings().memoryPreloadThreshold;
    }

    return memory > getSettings().memoryThreshold;
}

/**
 * Updates entire cache from fresh FrameInfo data. Call when receiving new data from native host.
 * (Interval poll, onIframeCreated, onIframeDestroyed)
 */
export function updateCacheFromFrameInfos(iframes: FrameInfoV2[] | undefined): void {
    scenarioMemoryCache.clear();

    if (!iframes || iframes.length === 0) {
        return;
    }

    // Group by scenario
    const iframesByScenario = new Map<IframeScenarioKey, FrameInfoV2[]>();
    for (const iframe of iframes) {
        const scenario = getScenarioFromIframeSource(iframe.iframeSource);
        const list = iframesByScenario.get(scenario) ?? [];
        list.push(iframe);
        iframesByScenario.set(scenario, list);
    }

    // Compute and cache memory for each scenario
    for (const [scenario, scenarioIframes] of iframesByScenario) {
        const memoryInfo = computeMemoryFromIframes(scenario, scenarioIframes);
        scenarioMemoryCache.set(scenario, memoryInfo);
    }

    if (scenarioMemoryCache.size > 0) {
        const results = Array.from(scenarioMemoryCache.values());
        trace.info(
            `Total iframe memory across ${results.length} scenarios:\n${results
                .map(r =>
                    formatMemoryInfo(r.scenario, r.totalMemory, r.totalCommitSize, r.processes)
                )
                .join('\n')}`,
            'iframeWrapper'
        );
    }
}

/**
 * Internal helper that computes memory info from pre-filtered iframes for a specific scenario.
 * @param scenario - The scenario to compute memory for
 * @param iframes - The filtered FrameInfoV2 array for the scenario
 * @returns Computed IframeMemoryInfo for this scenario
 */
function computeMemoryFromIframes(
    scenario: IframeScenarioKey,
    iframes: FrameInfoV2[]
): IframeMemoryInfo {
    const processes = new Map<ProcessId, ProcessMemory>();
    const frameMemoryMetricsList: FrameMemoryMetrics[] = [];
    for (const { processId, correlationId, workingSetSize, commitSize, iframeSource } of iframes) {
        if (!processes.has(processId)) {
            // don't accumulate workingSetSize across iframes in the same process
            processes.set(processId, {
                processId,
                memory: workingSetSize,
                commitSize,
                correlationIds: [],
            });
        }

        const processMemory = processes.get(processId);
        if (processMemory) {
            processMemory.correlationIds.push(correlationId);
        }

        const frameMemoryMetric: FrameMemoryMetrics = {
            workingSetSizeKB: workingSetSize * 1024,
            commitSizeKB: commitSize * 1024,
            hostAndPath: iframeSource,
            processId,
        };
        frameMemoryMetricsList.push(frameMemoryMetric);
    }

    let totalMemoryInMb = 0;
    let totalCommitSizeInMb = 0;
    for (const processMemory of processes.values()) {
        totalMemoryInMb += processMemory.memory;
        totalCommitSizeInMb += processMemory.commitSize;
    }

    trace.info(
        formatMemoryInfo(scenario, totalMemoryInMb, totalCommitSizeInMb, processes),
        'iframeWrapper'
    );

    return {
        scenario,
        totalMemory: totalMemoryInMb,
        totalCommitSize: totalCommitSizeInMb,
        processes,
        frameMemoryMetrics: frameMemoryMetricsList,
    };
}

/**
 * Computes memory info from FrameInfoV2 array for a specific scenario.
 * Filters iframes by scenario before computing.
 *
 * @param scenario - The scenario to compute memory for
 * @param iframes - The raw FrameInfoV2 array from native host
 * @returns Computed IframeMemoryInfo or undefined if no matching iframes
 */
export function computeMemoryFromFrameInfos(
    scenario: IframeScenario,
    iframes: FrameInfoV2[] | undefined
): IframeMemoryInfo | undefined {
    const matchedIframes = getIframesForScenarioV2(scenario, iframes);

    if (!matchedIframes || matchedIframes.length === 0) {
        return undefined;
    }

    return computeMemoryFromIframes(scenario, matchedIframes);
}

/**
 * Telemetry helpers
 */
export function getMemoryTelemetryFields(
    scenario: IframeScenario
): MemoryTelemetryFields | undefined {
    const memoryInfo = scenarioMemoryCache.get(scenario);
    if (!memoryInfo) {
        return undefined;
    }

    const processInfos = Array.from(memoryInfo.processes.entries()).map(
        ([processId, processMemory]) => ({
            processId,
            iframeCount: processMemory.correlationIds.length,
        })
    );

    const globalProcessCounts = new Map<number, number>();
    for (const scenarioMemory of scenarioMemoryCache.values()) {
        for (const [processId, processMemory] of scenarioMemory.processes) {
            const existing = globalProcessCounts.get(processId) ?? 0;
            globalProcessCounts.set(processId, existing + processMemory.correlationIds.length);
        }
    }
    const totalCounts = processInfos.map(p => globalProcessCounts.get(p.processId) ?? 0);

    return {
        totalScenarioMemory: memoryInfo.totalMemory,
        processCount: processInfos.length,
        processIds: processInfos.map(p => p.processId).join(','),
        scenarioIframeCounts: processInfos.map(p => p.iframeCount).join(','),
        totalIframeCounts: totalCounts.join(','),
    };
}

/**
 * Use this instead of manually spreading getIframeInfo() when logging telemetry.
 */
export function getIframeFullInfo(
    scenario: IframeScenario
): Partial<IframeInfo & MemoryTelemetryFields> {
    const stateInfo = getIframeInfo(scenario);
    const memoryFields = getMemoryTelemetryFields(scenario);

    if (!stateInfo && !memoryFields) {
        return {};
    }

    return {
        ...stateInfo,
        ...memoryFields,
    };
}

function formatMemoryInfo(
    scenario: IframeScenarioKey,
    totalMemory: number,
    totalCommitSize: number,
    processes: Map<ProcessId, ProcessMemory>
): string {
    const processDetails = Array.from(processes.values())
        .map(
            p =>
                `\tpid: ${p.processId}, memory: ${p.memory}MB, commitSize: ${
                    p.commitSize
                }MB, correlationIds: [${p.correlationIds.join(', ')}]`
        )
        .join('\n');

    return `IframeMemory {
    scenario: ${scenario},
    totalMemory: ${totalMemory}MB,
    totalCommitSize: ${totalCommitSize}MB,
    processCount: ${processes.size},
    processes:
${processDetails}
}`;
}

export function convertHighMemoryInfoToFrameInfo(highMemoryInfo: HighMemoryInfoV2): FrameInfoV2 {
    return {
        iframeId: highMemoryInfo.iframeId,
        iframeSource: highMemoryInfo.iframeSource,
        workingSetSize: highMemoryInfo.workingSetSizeMB,
        processId: highMemoryInfo.processId,
        correlationId: highMemoryInfo.correlationId,
        commitSize: highMemoryInfo.commitSizeMB,
    };
}
